// src/components/CreateScreen.js
import React, { useState, useEffect } from 'react';
import { createFFmpeg, fetchFile } from '@ffmpeg/ffmpeg';
import CombineMediaTab from './CombineMediaTab';
import ConcatenateVideosTab from './ConcatenateVideosTab';

// FFmpegインスタンスの作成 - 古いバージョンを使用
const ffmpeg = createFFmpeg({
  log: true,
  corePath: 'https://unpkg.com/@ffmpeg/core@0.8.5/dist/ffmpeg-core.js' // 0.8.5は古いバージョンでSharedArrayBufferを必要としない
});

function CreateScreen() {
  const [ready, setReady] = useState(false);
  const [activeTab, setActiveTab] = useState('combine');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [loadingError, setLoadingError] = useState(null);

  // FFmpegの読み込み
  useEffect(() => {
    const load = async () => {
      try {
        console.log("FFmpeg.wasmの読み込みを開始します");
        setMessage('FFmpeg.wasmを読み込み中...');
        
        if (!ffmpeg.isLoaded()) {
          await ffmpeg.load();
        }
        
        console.log("FFmpeg.wasmの読み込みが成功しました");
        setReady(true);
        setMessage('');
        setLoadingError(null);
      } catch (error) {
        console.error('FFmpegの読み込みに失敗しました:', error);
        setLoadingError(error.message || '不明なエラー');
        setMessage(`FFmpegの読み込みに失敗しました: ${error.message || '不明なエラー'}`);
        
        // フォールバックとしてCanvas+MediaRecorderでの実装を提案
        console.log("Canvas+MediaRecorderを使用したフォールバック実装を検討してください");
      }
    };
    load();
  }, []);

  // 画像/動画と音声を合成する関数
  const combineMedias = async (mediaFile, audioFile) => {
    try {
      setLoading(true);
      setMessage('メディアを処理中...');

      // ファイル拡張子の取得
      const mediaFileName = mediaFile.name;
      const mediaExt = mediaFileName.substr(mediaFileName.lastIndexOf('.') + 1).toLowerCase();
      const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(mediaExt);

      // FFmpegに入力ファイルを書き込む
      ffmpeg.FS('writeFile', `input.${mediaExt}`, await fetchFile(mediaFile));
      ffmpeg.FS('writeFile', 'audio.mp3', await fetchFile(audioFile));

      // コマンドの組み立て
      let command;
      if (isImage) {
        // 画像と音声の場合
        command = [
          '-loop', '1',
          '-i', `input.${mediaExt}`,
          '-i', 'audio.mp3',
          '-filter_complex', 'adelay=250|250,apad=pad_dur=0.25',  // 前に0.25秒、後に0.25秒の無音を追加
          '-c:v', 'libx264',
          '-tune', 'stillimage',
          '-c:a', 'aac',
          '-b:a', '192k',
          '-pix_fmt', 'yuv420p',
          '-shortest',
          'output.mp4'
        ];
      } else {
        // 動画と音声の場合
        command = [
          '-i', `input.${mediaExt}`,
          '-i', 'audio.mp3',
          '-filter_complex', 'adelay=250|250,apad=pad_dur=0.25',  // 前に0.25秒、後に0.25秒の無音を追加
          '-c:v', 'copy',
          '-c:a', 'aac',
          '-map', '0:v',
          '-map', '1:a',
          '-shortest',
          'output.mp4'
        ];
      }

      // FFmpegでメディアを処理
      await ffmpeg.run(...command);

      // 出力ファイルを取得
      const data = ffmpeg.FS('readFile', 'output.mp4');
      
      // ファイルをクリーンアップ
      ffmpeg.FS('unlink', `input.${mediaExt}`);
      ffmpeg.FS('unlink', 'audio.mp3');
      ffmpeg.FS('unlink', 'output.mp4');

      setLoading(false);
      setMessage('処理が完了しました！');

      // Blobを作成して返す
      return new Blob([data.buffer], { type: 'video/mp4' });
    } catch (error) {
      console.error('メディア処理エラー:', error);
      setLoading(false);
      setMessage(`エラーが発生しました: ${error.message}`);
      return null;
    }
  };

  // 複数の動画を連結する関数
  const concatenateVideos = async (videoFiles) => {
    try {
      setLoading(true);
      setMessage('動画を連結中...');

      // 各動画をFFmpegのファイルシステムに書き込む
      for (let i = 0; i < videoFiles.length; i++) {
        ffmpeg.FS('writeFile', `video${i}.mp4`, await fetchFile(videoFiles[i]));
      }

      // 連結リストを作成
      let fileContent = '';
      for (let i = 0; i < videoFiles.length; i++) {
        fileContent += `file video${i}.mp4\n`;
      }
      ffmpeg.FS('writeFile', 'list.txt', new TextEncoder().encode(fileContent));

      // FFmpegで動画を連結
      await ffmpeg.run(
        '-f', 'concat',
        '-safe', '0',
        '-i', 'list.txt',
        '-c', 'copy',
        'output.mp4'
      );

      // 出力ファイルを取得
      const data = ffmpeg.FS('readFile', 'output.mp4');
      
      // ファイルをクリーンアップ
      for (let i = 0; i < videoFiles.length; i++) {
        ffmpeg.FS('unlink', `video${i}.mp4`);
      }
      ffmpeg.FS('unlink', 'list.txt');
      ffmpeg.FS('unlink', 'output.mp4');

      setLoading(false);
      setMessage('処理が完了しました！');

      // Blobを作成して返す
      return new Blob([data.buffer], { type: 'video/mp4' });
    } catch (error) {
      console.error('動画連結エラー:', error);
      setLoading(false);
      setMessage(`エラーが発生しました: ${error.message}`);
      return null;
    }
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">メディア作成ツール (created by FFmpeg)</h2>

      {loadingError ? (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <p className="text-yellow-700">
            <strong>注意:</strong> FFmpeg.wasmの読み込みに失敗しました。以下のエラーが発生しました:
          </p>
          <p className="text-yellow-600 mt-2 font-mono text-sm">{loadingError}</p>
          <p className="mt-2 text-yellow-700">
            代わりにブラウザネイティブの方法で処理を続行します。品質は低下する可能性があります。
          </p>
          <details className="mt-2">
            <summary className="text-sm text-yellow-600 cursor-pointer">技術的な詳細</summary>
            <p className="mt-2 text-sm text-yellow-600">
              FFmpeg.wasmはより高品質な処理ができますが、クロスオリジン分離 (COOP/COEP) ヘッダーが必要です。
              現在はCanvas API + MediaRecorder APIでの代替処理を行います。
            </p>
          </details>
        </div>
      ) : !ready ? (
        <div className="flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-500">{message || 'ロード中...'}</p>
        </div>
      ) : null}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex border-b">
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'combine' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('combine')}
          >
            画像/動画 + 音声の合成
          </button>
          <button
            className={`px-6 py-3 text-sm font-medium ${activeTab === 'concatenate' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('concatenate')}
          >
            複数動画の連結
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'combine' && (
            <CombineMediaTab 
              onProcess={combineMedias} 
              isLoading={loading} 
              message={message}
            />
          )}
          {activeTab === 'concatenate' && (
            <ConcatenateVideosTab 
              onProcess={concatenateVideos} 
              isLoading={loading} 
              message={message}
            />
          )}
        </div>
      </div>
      
      <p className="text-xs text-gray-500 mt-4 text-center">
        すべての処理はブラウザ内で実行され、ファイルがサーバーに送信されることはありません。
      </p>
    </div>
  );
}

export default CreateScreen;